import { Box } from '@material-ui/core'
import { useOrderStore } from 'lib/hooks'
import { useEffect, useRef, useState } from 'react'
import { AutoSizer } from 'react-virtualized'
import { FixedSizeList, FixedSizeList as List } from 'react-window'
import { OrderArticle } from 'shared'
import { OrderVisualArticle } from './OrderVisualArticle'
import { PickingListSearch } from './PickingListSearch'

export interface PickingImageDisplayProps {
  type: string
  handlePick?: (article: OrderArticle, quantity: number) => void
  handleCompletePicking?: (article: OrderArticle, readonly: boolean) => void
  handleReportIssue?: (article: OrderArticle) => void
  handleBackToPicking?: (article: OrderArticle) => void
  handleIssues?: (a: OrderArticle) => void
  horizontalView?: boolean
}

export const PickingImageDisplay = ({
  type,
  handlePick,
  handleCompletePicking,
  handleReportIssue,
  handleBackToPicking,
  handleIssues,
  horizontalView,
}: PickingImageDisplayProps): JSX.Element => {
  const articlesToPick = useOrderStore((state) => state.articlesToPick)
  const articlesPicked = useOrderStore((state) => state.articlesPicked)

  const cardWidth = 212
  const spacing = 50
  const cardWithHorizontal = 340
  const itemWidth = (horizontalView ? cardWithHorizontal : cardWidth) + spacing

  const listRef = useRef<List>(null)

  const getArticles = () => (type === 'toPick' ? articlesToPick : articlesPicked)
  const articles = getArticles()

  const [searchText, setSearchText] = useState('')
  const [matchIndices, setMatchIndices] = useState<number[]>([])
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0)

  // Search effect
  useEffect(() => {
    if (!searchText.trim()) {
      setMatchIndices([])
      setCurrentMatchIndex(0)
      return
    }

    const lowerSearch = searchText.toLowerCase()
    const matches = articles.reduce<number[]>((acc, article, index) => {
      const nameMatch =
        article.getLocalizedName().toLowerCase().includes(lowerSearch) ||
        (article.card?.name ?? '').toLowerCase().includes(lowerSearch)
      if (nameMatch) acc.push(index)
      return acc
    }, [])

    setMatchIndices(matches)
    setCurrentMatchIndex(0)
  }, [searchText, articlesToPick, articlesPicked, type])

  // Scroll to current match
  useEffect(() => {
    if (matchIndices.length > 0 && listRef.current) {
      listRef.current.scrollToItem(matchIndices[currentMatchIndex], 'center')
    }
  }, [matchIndices, currentMatchIndex])

  // Keep the last picked card in view: picks are appended at the end.
  // Depends on the length because the array is mutated in place with push.
  const previousCount = useRef(0)
  useEffect(() => {
    if (type === 'picked' && articles.length > previousCount.current) {
      listRef.current?.scrollToItem(articles.length - 1, 'end')
    }
    previousCount.current = articles.length
  }, [articles.length, type])

  return (
    <>
      <PickingListSearch
        searchText={searchText}
        matchIndices={matchIndices}
        currentMatchIndex={currentMatchIndex}
        setSearchText={setSearchText}
        setCurrentMatchIndex={setCurrentMatchIndex}
      />
      <div style={{ height: horizontalView ? '268px' : '640px', width: '100%' }} className="mb-2">
        <AutoSizer>
          {({ width, height }) => (
            <FixedSizeList
              layout="horizontal"
              height={height}
              itemCount={articles.length}
              itemSize={itemWidth}
              itemData={articles}
              width={width}
              ref={listRef}
            >
              {({ index, style }: { index: number; style: any }) => {
                const isHighlighted = matchIndices.includes(index)

                return (
                  <Box style={style} className="p-1">
                    {type === 'toPick' && (
                      <OrderVisualArticle
                        article={articles[index]}
                        handlePick={handlePick}
                        handleCompletePicking={() => handleCompletePicking!(articles[index], false)}
                        handleReportIssue={handleReportIssue}
                        hideBar
                        horizontalView={horizontalView}
                        type={type}
                        highlightCard={isHighlighted}
                      />
                    )}

                    {type === 'picked' && (
                      <OrderVisualArticle
                        article={articles[index]}
                        handleBackToPicking={handleBackToPicking}
                        handleCompletePicking={() => handleCompletePicking!(articles[index], true)}
                        handleIssues={handleIssues}
                        hideBar
                        horizontalView={horizontalView}
                        type={type}
                        highlightCard={isHighlighted}
                        missingLocations={articles[index].locations.length === 0}
                      />
                    )}
                  </Box>
                )
              }}
            </FixedSizeList>
          )}
        </AutoSizer>
      </div>
    </>
  )
}
